import {
  Component,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react"
import type { FC, PropsWithChildren, ReactNode } from "react"
import { Outlet, useNavigate } from "react-router"
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import type { AppState as Auth0AppState } from "@auth0/auth0-react"
import {
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from "react-oidc-context"
import type { AuthProviderProps } from "react-oidc-context"
import { WebStorageStateStore } from "oidc-client-ts"
import type { User as OidcUser } from "oidc-client-ts"
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REALM,
  KEYCLOAK_URL,
} from "./env"

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthUser {
  email?: string
  name?: string
  picture?: string
  org_id?: string
  [claim: string]: unknown
}

export interface LoginOptions {
  login_hint?: string
  returnTo?: string
}

export interface LogoutOptions {
  returnTo?: string
}

/** Normalized error shape — replaces Auth0's OAuthError in consumer code. */
export interface AuthProviderError {
  error: string
  error_description?: string
}

export interface AuthInterface {
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | AuthProviderError | undefined
  user: AuthUser | undefined
  loginWithRedirect: (options?: LoginOptions) => Promise<void>
  logout: (options?: LogoutOptions) => Promise<void>
  getAccessTokenSilently: () => Promise<string>
  getIdTokenClaims: () => Promise<AuthUser | undefined>
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider selection
// ─────────────────────────────────────────────────────────────────────────────

export const AUTH_PROVIDER_KEY = "auth_provider"
export type AuthProvider = "auth0" | "keycloak"

/**
 * True only when all three Keycloak env vars are present. On production these
 * are never set, so Keycloak is unconditionally disabled there regardless of
 * any localStorage value a developer may have left behind.
 */
export const KEYCLOAK_ENABLED = Boolean(
  KEYCLOAK_URL && KEYCLOAK_REALM && KEYCLOAK_CLIENT_ID
)

/**
 * Resolved once at module load. Reads `?auth_provider=` from the URL first,
 * which acts as an escape hatch if Keycloak is misconfigured — navigate to
 * the app with `?auth_provider=auth0` to reset back to Auth0.
 */
function resolveAuthProvider(): AuthProvider {
  if (typeof window === "undefined" || !KEYCLOAK_ENABLED) return "auth0"
  try {
    const params = new URLSearchParams(window.location.search)
    const urlProvider = params.get(AUTH_PROVIDER_KEY)
    if (urlProvider === "auth0" || urlProvider === "keycloak") {
      localStorage.setItem(AUTH_PROVIDER_KEY, urlProvider)
      params.delete(AUTH_PROVIDER_KEY)
      const newSearch = params.toString() ? `?${params.toString()}` : ""
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${newSearch}${window.location.hash}`
      )
      return urlProvider
    }
    return localStorage.getItem(AUTH_PROVIDER_KEY) === "keycloak"
      ? "keycloak"
      : "auth0"
  } catch {
    return "auth0"
  }
}

const activeProvider = resolveAuthProvider()

// ─────────────────────────────────────────────────────────────────────────────
// Auth context
// ─────────────────────────────────────────────────────────────────────────────

export const AuthContext = createContext<AuthInterface | null>(null)

/**
 * Returns the current auth state and methods from whichever provider is active.
 */
const useAuth = (): AuthInterface => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthWrapper")
  return ctx
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth0 adapter
// ─────────────────────────────────────────────────────────────────────────────

const Auth0AuthInner: FC = () => {
  const auth0 = useAuth0()

  const value = useMemo<AuthInterface>(
    () => ({
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      error: auth0.error,
      user: auth0.user as AuthUser | undefined,
      loginWithRedirect: opts =>
        auth0.loginWithRedirect({
          authorizationParams: { login_hint: opts?.login_hint },
          appState: { returnTo: opts?.returnTo },
        }),
      logout: opts =>
        auth0.logout({
          logoutParams: { returnTo: opts?.returnTo ?? window.location.origin },
        }),

      getAccessTokenSilently: () => auth0.getAccessTokenSilently(),
      getIdTokenClaims: async () => {
        const idToken = await auth0.getIdTokenClaims()
        return idToken as AuthUser | undefined
      },
    }),
    [auth0]
  )

  return (
    <AuthContext value={value}>
      <Outlet />
    </AuthContext>
  )
}

const Auth0AuthWrapper: FC = () => {
  const navigate = useNavigate()
  const onRedirectCallback = useCallback(
    (appState?: Auth0AppState) => {
      void navigate(appState?.returnTo ?? window.location.pathname)
    },
    [navigate]
  )

  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
    >
      <Auth0AuthInner />
    </Auth0Provider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Keycloak / OIDC adapter
// ─────────────────────────────────────────────────────────────────────────────

const KeycloakAuthInner: FC = () => {
  const oidcAuth = useOidcAuth()

  const value = useMemo<AuthInterface>(
    () => ({
      isAuthenticated: oidcAuth.isAuthenticated,
      isLoading: oidcAuth.isLoading,
      error: oidcAuth.error,
      user: oidcAuth.user?.profile as AuthUser | undefined,
      loginWithRedirect: opts =>
        oidcAuth.signinRedirect({
          login_hint: opts?.login_hint,
          state: { returnTo: opts?.returnTo },
        }),
      logout: opts =>
        oidcAuth.signoutRedirect({
          post_logout_redirect_uri: opts?.returnTo ?? window.location.origin,
        }),

      getAccessTokenSilently: () => {
        if (oidcAuth.user && !oidcAuth.user.expired) {
          return Promise.resolve(oidcAuth.user.access_token)
        }
        // Reject with the same shape as Auth0's OAuthError so callers that
        // inspect `error.error` (e.g. useGetPermissions) handle it correctly.
        return Promise.reject(
          Object.assign(new Error("login_required"), {
            error: "login_required",
          })
        )
      },
      getIdTokenClaims: () =>
        Promise.resolve(oidcAuth.user?.profile as AuthUser | undefined),
    }),
    [oidcAuth]
  )

  return (
    <AuthContext value={value}>
      <Outlet />
    </AuthContext>
  )
}

const KeycloakAuthWrapper: FC = () => {
  const navigate = useNavigate()

  const oidcConfig = useMemo<AuthProviderProps>(
    () => ({
      authority: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`,
      client_id: KEYCLOAK_CLIENT_ID,
      redirect_uri: window.location.origin,
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      onSigninCallback: (user: OidcUser | undefined) => {
        // Strip OIDC callback params from the URL after a successful redirect
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        )
        const returnTo = (user?.state as { returnTo?: string } | undefined)
          ?.returnTo
        if (returnTo) void navigate(returnTo, { replace: true })
      },
    }),
    [navigate]
  )

  return (
    <OidcAuthProvider {...oidcConfig}>
      <KeycloakAuthInner />
    </OidcAuthProvider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Error boundary — catches Keycloak init failures, auto-resets to Auth0
// ─────────────────────────────────────────────────────────────────────────────

interface KCBoundaryState {
  failed: boolean
}

class KeycloakFallbackBoundary extends Component<
  PropsWithChildren,
  KCBoundaryState
> {
  state: KCBoundaryState = { failed: false }

  static getDerivedStateFromError(): KCBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: unknown): void {
    console.error(
      "[Keycloak] Provider initialization failed; falling back to Auth0:",
      error
    )
    localStorage.removeItem(AUTH_PROVIDER_KEY)
  }

  render(): ReactNode {
    if (this.state.failed) {
      return <Auth0AuthWrapper />
    }
    return this.props.children
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified AuthWrapper (route element)
// ─────────────────────────────────────────────────────────────────────────────

export const AuthWrapper: FC =
  activeProvider === "keycloak"
    ? () => (
        <KeycloakFallbackBoundary>
          <KeycloakAuthWrapper />
        </KeycloakFallbackBoundary>
      )
    : Auth0AuthWrapper

export default useAuth
